import { create } from 'zustand';
import type { Store, GraphInstanceGetter } from './types';
import { isDirtyTrackingSuppressed } from './dirtyTracking';
import {
  createMaterialActions,
  createOperationActions,
  createEdgeActions,
  createGroupActions,
  createSelectionActions,
  createModalActions,
  createUiActions,
  createSnapshotActions,
  createStepActions,
  createBatchStepActions,
  createTransferActions,
  createComplexActions,
  createEditActions,
  createArchetypeConversionActions,
  createDeleteActions,
  createHistoryActions,
  createZoomActions,
  createInsertActions,
  createHighlightActions,
  createLinkActions,
  createMergeActions,
} from './actions';

let graphGetter: GraphInstanceGetter | null = null;

export const useGraphEditorStore = create<Store>()((set, get) => ({
  // Initial state
  graphData: {
    materialNodes: {},
    operationNodes: {},
    flowEdges: {},
    groups: {},
    graphComments: [],
  },
  selection: { cellIds: [] },
  modals: { stack: [] },
  ui: { 
    isDirty: false, 
    isReadOnly: false, 
    isLoading: false, 
    currentUser: null, 
    versionId: null,
    isProtocolLocked: false,
    isLifecycleLocked: false,
    isArchived: false,
    isArchivalInProgress: false,
    runsExist: false,
    lockReason: null,
    canEditContent: true,
  },
  sync: { syncInProgress: false },
  history: { undoStack: [], redoStack: [] },
  highlights: {
    visible: {
      comments: false,
      notes: false,
      checkpoints: false,
      notifications: false,
      approvals: false,
    },
  },

  // Graph instance registration
  _registerGraphGetter(getter) {
    graphGetter = getter;
  },

  // Actions — each factory receives set, get, and a lazy graph getter
  ...createMaterialActions(set, get, () => graphGetter?.() ?? null),
  ...createOperationActions(set, get, () => graphGetter?.() ?? null),
  ...createEdgeActions(set, get, () => graphGetter?.() ?? null),
  ...createGroupActions(set, get, () => graphGetter?.() ?? null),
  ...createSelectionActions(set, get, () => graphGetter?.() ?? null),
  ...createModalActions(set),
  ...createStepActions(get),
  ...createBatchStepActions(get),
  ...createTransferActions(set, get, () => graphGetter?.() ?? null),
  ...createComplexActions(get),
  ...createInsertActions(set, get, () => graphGetter?.() ?? null),
  ...createUiActions(set, get),
  ...createEditActions(set, get, () => graphGetter?.() ?? null),
  ...createArchetypeConversionActions(set, get, () => graphGetter?.() ?? null),
  ...createDeleteActions(set, get, () => graphGetter?.() ?? null),
  ...createHistoryActions(set, get),
  ...createZoomActions(() => graphGetter?.() ?? null),
  ...createSnapshotActions(set, get, () => graphGetter?.() ?? null),
  ...createHighlightActions(set, get),
  ...createLinkActions(set, get, () => graphGetter?.() ?? null),
  ...createMergeActions(set, get, () => graphGetter?.() ?? null),

  // Internal sync actions
  _applyGraphSelectionChange(cellIds: string[]) {
    set((state) => ({ selection: { ...state.selection, cellIds } }));
  },

  _applyGraphCellsRemoved(cellIds: string[]) {
    set((state) => {
      const materialNodes = { ...state.graphData.materialNodes };
      const operationNodes = { ...state.graphData.operationNodes };
      const flowEdges = { ...state.graphData.flowEdges };
      const groups = { ...state.graphData.groups };

      for (const id of cellIds) {
        delete materialNodes[id];
        delete operationNodes[id];
        delete flowEdges[id];
        delete groups[id];
      }

      return {
        graphData: { ...state.graphData, materialNodes, operationNodes, flowEdges, groups },
        ui: { ...state.ui, isDirty: true },
      };
    });
  },

  // Sync collapsed state from maxGraph fold button → store (no undo push,
  // no graph write-back since the fold already happened in maxGraph).
  _applyGroupCollapsed(groupId: string, isCollapsed: boolean) {
    set((state) => {
      const group = state.graphData.groups[groupId];
      if (!group) return state;
      return {
        graphData: {
          ...state.graphData,
          groups: {
            ...state.graphData.groups,
            [groupId]: { ...group, isCollapsed },
          },
        },
      };
    });
  },
}));

// Centralized dirty tracking safety net: if any change touches graphData
// (operation/material/edge/group content) but leaves `ui` untouched, mark the
// graph dirty so Save is enabled — no matter which path made the change.
//
// Edits/loads/resets all replace `ui` themselves (edits set isDirty:true; loads
// and resets set isDirty:false), so `state.ui === prev.ui` isolates exactly the
// "changed content but forgot to flag dirty" case. Load-time mutations also run
// inside runWithoutDirtyTracking (see snapshotActions) as a second guard.
useGraphEditorStore.subscribe((state, prev) => {
  if (isDirtyTrackingSuppressed()) return;
  if (state.graphData === prev.graphData) return; // no content change
  if (state.ui !== prev.ui) return; // caller managed the dirty flag explicitly
  if (state.ui.isDirty || state.ui.isLoading) return;
  useGraphEditorStore.setState((s) => ({ ui: { ...s.ui, isDirty: true } }));
});
